import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Save, Eye, EyeOff, Wifi, WifiOff, AlertTriangle,
  CheckCircle2, Info,
} from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.37 · Panel Configuración > Seguridad > reCAPTCHA
// Reglas de oro:
// - super_admin nunca es bloqueado.
// - Env var RECAPTCHA_ENABLED tiene prioridad (killswitch).
// - Si Google falla, fail-open + audit + alerta.
// El panel siempre muestra el override y el estado efectivo con claridad.

export default function RecaptchaSettings() {
  const pin = usePinSecurity();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const [form, setForm] = useState({
    enabled: false, version: "v2", site_key: "", secret_key: "", min_score: 0.5,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/recaptcha/status");
      setState(data);
      setForm({
        enabled: data.enabled,
        version: data.version || "v2",
        site_key: data.site_key || "",
        secret_key: "",       // nunca precargamos la clave — el usuario debe reintroducirla para cambiarla
        min_score: data.min_score ?? 0.5,
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await pin.require("setting.update");
      const patch = {
        enabled: form.enabled,
        version: form.version,
        site_key: form.site_key.trim(),
        min_score: Number(form.min_score) || 0.5,
      };
      // Solo enviamos secret_key si el usuario tecleó algo (para no borrarla)
      if (form.secret_key.trim()) {
        patch.secret_key = form.secret_key.trim();
      }
      const { data } = await api.patch("/security/recaptcha/config", patch);
      setState(data);
      setForm((f) => ({ ...f, secret_key: "" }));  // limpiamos por seguridad
      toast.success("Configuración guardada");
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await pin.require("setting.update");
      const { data } = await api.post("/security/recaptcha/test");
      setTestResult(data);
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    } finally {
      setTesting(false);
    }
  };

  if (loading || !state) {
    return (
      <div className="card-surface p-4 text-sm text-neutral-500">
        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  const envOverride = state.env_override;
  const effective = state.effective_enabled;

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="recaptcha-settings">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="flex-1">
          <h3 className="font-semibold text-base leading-tight">
            Google reCAPTCHA {state.version === "v3" ? "v3" : "v2"} (V1.37)
          </h3>
          <p className="text-xs text-neutral-500 leading-snug mt-0.5">
            Protección del login contra bots. El <b>super_admin nunca</b> queda
            bloqueado, y si Google falla el sistema permite el acceso auditado.
          </p>
        </div>
        <EffectiveBadge effective={effective} envOverride={envOverride} />
      </div>

      {/* Aviso del env override */}
      {envOverride !== null && (
        <div className={
          "mb-3 rounded-lg border p-2.5 text-xs flex items-start gap-2 " +
          (envOverride
            ? "border-amber-300 bg-amber-50 text-amber-800"
            : "border-red-300 bg-red-50 text-red-800")
        }>
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <b>Variable de entorno activa:</b> <code>RECAPTCHA_ENABLED={String(envOverride)}</code>
            {" "}— tiene prioridad sobre esta configuración.
            {envOverride
              ? " reCAPTCHA está forzado ON en todo el sistema."
              : " reCAPTCHA está DESACTIVADO por killswitch de emergencia."}
          </div>
        </div>
      )}

      {/* V1.37 · Salvaguarda: no permitir activar sin super_admin */}
      {state.can_enable === false && (
        <div
          className="mb-3 rounded-lg border border-red-300 bg-red-50 text-red-800 p-3 text-xs flex items-start gap-2"
          data-testid="recaptcha-no-super-admin-warning"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />
          <div>
            <b>No se puede activar reCAPTCHA.</b><br />
            No existe ninguna cuenta con <code>is_super_admin=true</code> en la base de datos.
            Sin un super_admin, si Google fallara nadie podría recuperar el acceso.
            Configure un super_admin antes de activar esta protección.
          </div>
        </div>
      )}

      {/* Toggle */}
      <label
        className={
          "flex items-center gap-2 text-sm mb-3 " +
          (state.can_enable === false ? "opacity-60 cursor-not-allowed" : "cursor-pointer select-none")
        }
      >
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          disabled={state.can_enable === false}
          data-testid="recaptcha-enabled-toggle"
        />
        <span className="font-semibold">
          Activar reCAPTCHA v3 en el inicio de sesión
        </span>
      </label>

      {/* Keys */}
      <div className="mb-3">
        <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
          Tipo de reCAPTCHA
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setForm({ ...form, version: "v2" })}
            data-testid="recaptcha-version-v2"
            className={
              "rounded-xl border p-3 text-left transition-colors " +
              (form.version === "v2"
                ? "border-emerald-500 bg-emerald-50"
                : "border-neutral-200 hover:border-emerald-300 bg-white")
            }
          >
            <div className="font-bold text-sm">v2 · Checkbox</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              &ldquo;No soy un robot&rdquo; — widget visible
            </div>
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, version: "v3" })}
            data-testid="recaptcha-version-v3"
            className={
              "rounded-xl border p-3 text-left transition-colors " +
              (form.version === "v3"
                ? "border-emerald-500 bg-emerald-50"
                : "border-neutral-200 hover:border-emerald-300 bg-white")
            }
          >
            <div className="font-bold text-sm">v3 · Invisible</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Score 0-1, sin interacción
            </div>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Site Key (público)">
          <input
            type="text"
            value={form.site_key}
            onChange={(e) => setForm({ ...form, site_key: e.target.value })}
            placeholder="6Lc..."
            className={fieldCls}
            data-testid="recaptcha-site-key"
          />
        </Field>
        <Field label="Secret Key (privado, se enmascara)">
          <div className="relative">
            <input
              type={showSecret ? "text" : "password"}
              value={form.secret_key}
              onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              placeholder={
                state.secret_key_configured
                  ? `Configurada: ${state.secret_key_masked} — deja en blanco para mantenerla`
                  : "6Lc..."
              }
              className={fieldCls + " pr-10"}
              data-testid="recaptcha-secret-key"
            />
            <button type="button"
                    onClick={() => setShowSecret((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>
      </div>
      {form.version === "v3" && (
        <Field label="Puntuación mínima (score) para aceptar login">
          <input
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={form.min_score}
            onChange={(e) => setForm({ ...form, min_score: e.target.value })}
            className={fieldCls + " w-32"}
            data-testid="recaptcha-min-score"
          />
          <div className="text-[10px] text-neutral-400 mt-1">
            0.0 = permisivo · 0.5 = recomendado · 1.0 = solo tráfico humano perfecto
          </div>
        </Field>
      )}

      {/* Estado */}
      <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 text-xs space-y-1">
        <Row label="Estado en base de datos">
          {state.enabled
            ? <span className="text-emerald-700 font-semibold">Activado</span>
            : <span className="text-neutral-500">Desactivado</span>}
        </Row>
        <Row label="Estado efectivo">
          {effective
            ? <span className="text-emerald-700 font-semibold">🔒 reCAPTCHA activo</span>
            : <span className="text-neutral-500">⛌ No se pedirá token</span>}
        </Row>
        <Row label="Secret configurada">
          {state.secret_key_configured
            ? <span className="text-emerald-700 font-mono">{state.secret_key_masked}</span>
            : <span className="text-red-600">— No configurada</span>}
        </Row>
        {state.last_success_at && (
          <Row label="Última verificación OK">
            <span className="text-neutral-600 tabular-nums">{state.last_success_at}</span>
          </Row>
        )}
        {state.last_error && (
          <Row label="Último error">
            <span className="text-red-700 text-[11px]">
              {state.last_error} <span className="text-neutral-400">· {state.last_error_at}</span>
            </span>
          </Row>
        )}
      </div>

      {/* Test result */}
      {testResult && (
        <div className={
          "mt-3 rounded-lg border p-2.5 text-xs flex items-start gap-2 " +
          (testResult.connected
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-red-300 bg-red-50 text-red-800")
        } data-testid="recaptcha-test-result">
          {testResult.connected
            ? <Wifi className="h-4 w-4 mt-0.5 shrink-0" />
            : <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />}
          <div>
            <b>{testResult.connected ? "Conexión con Google OK" : "Sin conexión con Google"}</b>
            {testResult.note && <> · {testResult.note}</>}
            {testResult.reason && <> · {testResult.reason}</>}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary !h-10 !px-4 !text-sm"
          data-testid="recaptcha-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar configuración
        </button>
        <button
          type="button"
          onClick={runTest}
          disabled={testing || !state.secret_key_configured}
          className="btn-secondary !h-10 !px-4 !text-sm"
          data-testid="recaptcha-test-btn"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
          Probar conexión con Google
        </button>
      </div>

      <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-3 text-[11px] text-neutral-600 leading-relaxed space-y-1"
           data-testid="recaptcha-safety-notes">
        <div className="font-bold text-neutral-800 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-600" />
          Garantías de seguridad (no se puede desactivar)
        </div>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>Super Admin</b> nunca es bloqueado por reCAPTCHA, aunque falle Google.</li>
          <li>Si Google no responde en 4s o devuelve error, el login se permite y se audita.</li>
          <li>La variable de entorno <code>RECAPTCHA_ENABLED=false</code> apaga reCAPTCHA en todo el sistema (killswitch).</li>
          <li>Todos los cambios de esta configuración quedan auditados.</li>
        </ul>
      </div>
    </div>
  );
}

function EffectiveBadge({ effective, envOverride }) {
  let tone, icon, label;
  if (envOverride === false) {
    tone = "bg-red-100 text-red-800 border-red-300";
    icon = <ShieldCheck className="h-3 w-3" />;
    label = "OFF (env)";
  } else if (effective) {
    tone = "bg-emerald-100 text-emerald-800 border-emerald-300";
    icon = <CheckCircle2 className="h-3 w-3" />;
    label = "ACTIVO";
  } else {
    tone = "bg-neutral-100 text-neutral-500 border-neutral-200";
    icon = <ShieldCheck className="h-3 w-3" />;
    label = "INACTIVO";
  }
  return (
    <span className={
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold " + tone
    }>
      {icon} {label}
    </span>
  );
}

const fieldCls =
  "w-full h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-neutral-50";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-bold">{label}</span>
      <span>{children}</span>
    </div>
  );
}
